import React from 'react';
import { Tooltip, Nav, OverlayTrigger } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import Flex from 'components/common/Flex';
import styles from 'assets/scss/wizard/wizardLayoutEmet.module.scss';

/**
 * @component NavItemPill
 * @description
 * Componente visual que representa una "píldora" de navegación dentro del flujo de un examen tipo wizard.
 * Indica el estado del paso (activo, completado o pendiente), muestra un ícono y una etiqueta, y si aplica,
 * despliega un tooltip con la descripción de la sección. El paso "BREAK" se muestra sin tooltip.
 *
 * @param {Object} props - Props del componente.
 * @param {number} props.index - Índice del paso representado por esta píldora.
 * @param {number} props.step - Paso actual del examen; se usa para determinar si esta píldora está activa o completada.
 * @param {Function} props.handleNavs - Función que se llama cuando se hace clic en la píldora para cambiar de paso.
 * @param {object} props.icon - Ícono de FontAwesome que representa la sección.
 * @param {string} props.label - Texto que describe la sección (por ejemplo: "SPEAKING", "BREAK").
 * @param {string} props.sectionDesc - Texto descriptivo que se muestra como tooltip (excepto en pasos BREAK).
 *
 * @returns {JSX.Element} Píldora de navegación con ícono, texto y tooltip opcional.
 *
 * @example
 * <NavItemPill
 *   index={2}
 *   step={currentStep}
 *   handleNavs={goToStep}
 *   icon={faPen}
 *   label="WRITING"
 *   sectionDesc="Sección de escritura"
 * />
 */
const NavItemPill = ({
	index,
	step,
	handleNavs,
	icon,
	label,
	sectionDesc
}) => {
	const sectionTooltip = (
		<Tooltip id="tooltip" className={styles.customTooltip}>
			{sectionDesc}
		</Tooltip>
	);

	const navLinkContent = (
		<Nav.Link
			className={classNames('fw-semi-bold', {
				done: step > index,
				active: step === index
			})}
			onClick={() => handleNavs(index)} >
			<Flex alignItems="center" justifyContent="center">
				<FontAwesomeIcon icon={icon} />
				<span className="d-none d-md-block mt-1 fs--1 ms-2">{label}</span>
			</Flex>
		</Nav.Link>
	);

	return (
		<Nav.Item>
			{label.trim() === 'BREAK' ? navLinkContent : (
				<OverlayTrigger placement="bottom" overlay={sectionTooltip}>
					{navLinkContent}
				</OverlayTrigger>
			)}
		</Nav.Item>
	);
};

export default NavItemPill;